import { Component, Injector, OnDestroy, OnInit, ViewEncapsulation, signal } from '@angular/core';

import { AppInfoConfig, ClaimsService, ClientConfigProductName, ClientConfigService, Page } from '@frontend/vanilla/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthDetails, AuthDetailsStore, CashierType, CustomHttpClientService, ProductResolverService } from '@payments/common-utils';

import { AssetsData, PaymentsCashierConfig } from './client-config/client-config.models';
// import { AppConstants } from './config/appConstants';
import { LoaderComponent } from './core/loader/loader.component';
import { LoaderInterceptor } from './core/loader/loader.interceptor';
import { QDComponent } from './quick-deposit/qd.component';
import { CardIframeService } from './service/card-iframe-service';
import { CustomTranslateLoaderService } from './service/custom-translate-loader.service';
import { DataService } from './service/data.service';
import { QdAuthenticationService } from './service/qd-authentication.service';
import { QuickDepositMessageService } from './service/quick-deposit-message.service';
import { ResizeService } from './service/resize.service';

/**
 * @whatItDoes Provides QuickDeposit component for users to deposit
 *
 * @howToUse
 *
 * ```
 * // Include the QuickDepositFeatureComponent in your application
 * <qd-feature></qd-feature>
 *
 * // Inject the QuickDepositMessageService to interact with the component
 * import { QuickDepositMessageService } from '@payments/quickdeposit-feature';
 *
 * export class MyComponent {
 *     constructor(private quickDepositMessageService: QuickDepositMessageService) {}
 *
 *     handleMessages() {
 *         const outputMessage = this.quickDepositMessageService.output();
 *         if (outputMessage.eventName) {
 *             this.quickDepositService.handleCashierMessage({ data: outputMessage } as MessageEvent);
 *         }
 *     }
 *
 *     sendDepositMessage() {
 *         // Parent sends messages to the component
 *         this.quickDepositMessageService.sendInput({ eventName: 'Deposit' });
 *     }
 * }
 * ```
 *
 * @description
 *
 * The `QuickDepositFeatureComponent` is a standalone Angular component that allows users to perform quick deposits. It accepts a `stake` input in cents and integrates seamlessly into your application.
 *
 * ## Component
 *
 * - **Selector**: `<qd-feature></qd-feature>`
 * - **Input**: `stake` (number) - The deposit value in cents.
 *
 * ## Service
 *
 * The `QuickDepositMessageService` enables communication with the component for sending and receiving messages.
 *
 * ### Methods
 *
 * - `output()`: Retrieves the latest message output from the component.
 * Example Usage:
 * ```typescript
 * effect(
 *     () => {
 *         const outputMessage = this.quickDepositMessageService.output();
 *         if (outputMessage.eventName) {
 *             this.quickDepositService.handleCashierMessage({ data: outputMessage } as MessageEvent);
 *         }
 *     },
 * );
 * ```
 * - `sendInput(input: object)`: Sends a message or input to the component.
 * Example Usage:
 *   ```typescript
 *   this.quickDepositMessageService.sendInput({ eventName: 'Deposit' });
 *   ```
 *
 *
 * ## Features
 *
 * - Seamlessly integrate quick deposit functionality into your app.
 * - Use `QuickDepositMessageService` for efficient communication and handling of component events.
 * - Customizable stake input to define default deposit values.
 *
 * @stable
 */

@Component({
    selector: 'qd-feature',
    templateUrl: './quick-deposit-feature.component.html',
    styleUrl: './quick-deposit-feature.component.scss',
    imports: [QDComponent, LoaderComponent, TranslateModule],
    encapsulation: ViewEncapsulation.None,
    providers: [
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoaderService,
            },
        }).providers ?? [],
    ],
})
export class QuickDepositFeatureComponent implements OnInit, OnDestroy {
    redesign = signal<boolean>(false);
    login = signal<boolean>(false);
    sessionInfo = signal<{ [key: string]: any }>({});
    authDetails = signal<AuthDetails>({
        authCode: '',
        authFlag: false,
        cashierType: undefined,
    });

    constructor(
        private authService: QdAuthenticationService,
        private dataService: DataService,
        private translateService: TranslateService,
        private resizer: ResizeService,
        private page: Page,
        private appInfo: AppInfoConfig,
        private claimsService: ClaimsService,
        private clientConfigService: ClientConfigService,
        private injector: Injector,
        private cardIframeService: CardIframeService,
        private authDetailsStore: AuthDetailsStore,
        private customHttpClientService: CustomHttpClientService,
        private quickDepositMessageService: QuickDepositMessageService,
        private productResolverService: ProductResolverService,
    ) {
        this.customHttpClientService.initialize([new LoaderInterceptor()]);
        this.authDetailsStore.initialize();
    }

    ngOnInit(): void {
        this.resizer.resizeCashier();
        this.clientConfigService
            .loadProduct(ClientConfigProductName.PAYMENTS)
            .then(() => {
                this.injector.get(AssetsData);
                this.injector.get(PaymentsCashierConfig);
                this.setParams();
                this.cardIframeService.initialize();
            })
            .catch((error) => {
                console.error('Error loading product:', error);
            });
    }

    async setParams() {
        const productId = await this.productResolverService.getProduct();

        this.translateService.use(this.page.lang);
        const sessionInfoTemp: { [key: string]: string } = {};
        sessionInfoTemp['brandId'] = this.appInfo.brand;
        sessionInfoTemp['channelId'] = this.appInfo.channel;
        sessionInfoTemp['productId'] = productId;
        sessionInfoTemp['LANG_ID'] = this.page.lang;
        sessionInfoTemp['sessionKey'] = this.claimsService.get('http://api.bwin.com/v3/user/ssotoken') ?? '';
        sessionInfoTemp['userId'] = this.claimsService.get('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier') ?? '';

        this.dataService.sessionInfo = sessionInfoTemp;
        this.sessionInfo.set(sessionInfoTemp);
        await this.authenticateUser();
    }

    async authenticateUser() {
        const { LANG_ID, ...authenticationInfo } = this.sessionInfo();
        await this.authService.authenticateUser(authenticationInfo).subscribe(
            (data: any) => {
                if (data) {
                    if (data['body']) {
                        this.authDetailsStore.updateAuthDetails({
                            authCode: data['headers'].get('auth-code'),
                            authFlag: true,
                            cashierType: CashierType.QdOverlay,
                        });
                    }
                    this.authDetails.set({ authCode: data['headers'].get('auth-code'), authFlag: data['body'], cashierType: CashierType.QdOverlay });
                    this.redesign.set(true);
                } else {
                    this.login.set(true);
                }
            },
            (error: any) => {
                this.dataService.error.set(error);
                this.login.set(error.status === 401);
            },
        );
    }

    onResize() {
        this.resizer.resizeCashier();
    }

    ngOnDestroy(): void {
        this.authDetailsStore.initialize();
        this.quickDepositMessageService.destroy();
    }
}
